import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { LoadErrorState } from '../../states/index';
import { InviteMemberModal } from '../../group/InviteMemberModal';
import { GroupPendingInvitesCard } from '../../group/admin/GroupPendingInvitesCard';
import { CopyJoinLinkButton } from '../../group/admin/CopyJoinLinkButton';
import { GroupArchivedCallout } from '../../group/GroupArchivedCallout';
import type { GroupRosterMember } from '../../../lib/views/groups';
import { useCircle, useGroupRoster } from '../../../lib/queries/groups';
import { useFormatDate } from '../../../lib/format-date.client';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useErrorMessage } from '../../../lib/error-message';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import {
  MembersTablePanel,
  MembersTableSkeleton,
  buildRosterColumns,
  memberName,
} from '../../group/admin/members-table';
import { useRosterExport } from '../../group/admin/use-roster-export';

type Props = {
  readonly circleId: string;
  readonly viewerUserId: string;
  /** Open the invite modal on mount (`?invite=1` deep link). */
  readonly initialInviteOpen?: boolean;
};

export function CircleMembersSection({ circleId, viewerUserId, initialInviteOpen = false }: Props) {
  const t = useTranslations('Group');
  const tc = useTranslations('Circles');
  const describeError = useErrorMessage();
  const detailQuery = useCircle(circleId);
  const membersQuery = useGroupRoster(circleId);
  const fmt = useFormatDate();
  const circleName =
    detailQuery.data && detailQuery.data.access !== 'visitor'
      ? detailQuery.data.circle.name
      : '';
  // Archived Circles are read-only: the roster stays viewable (the server now
  // grants an archived Circle's leader read access) but every management
  // affordance is withdrawn behind a Restore callout. `status` is present on
  // both the visitor `CirclePreview` and the member/leader `GroupDetail`.
  const isArchived = detailQuery.data?.circle.status === 'archived';
  const exportRoster = useRosterExport(circleName, 'circle');

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id: circleId, kind: 'circle' },
        {
          onSuccess: () => {
            // Land back on /circles since the viewer is no longer a member.
            navigate({ to: '/circles' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: tc('membersAdmin.errorLeave'),
  });

  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(initialInviteOpen);
  const remove = useConfirmDialog<GroupRosterMember>();
  const demote = useConfirmDialog<GroupRosterMember>();

  const isPending =
    setRoleMutation.isPending ||
    removeMutation.isPending;

  const handlePromote = (m: GroupRosterMember) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: circleId, kind: 'circle', userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tc('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: circleId, kind: 'circle', userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tc('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: circleId, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, describeError, tc('membersAdmin.errorRemove'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const visibleError = error;

  // Gate the body on BOTH queries: `isArchived` comes from the detail query,
  // so rendering management affordances before it settles would flash the
  // active-group controls on an archived Circle (the roster query can win the
  // race on a cold deep-link). `isLoading` is the initial fetch only, so a
  // failed detail (data stays undefined but not loading) still renders — the
  // established fallback where the section shows without the archived state.
  const showSkeleton = useDelayedLoading(
    (membersQuery.data === undefined && !membersQuery.isError) ||
      detailQuery.isLoading,
  );
  if (membersQuery.isError) {
    return <LoadErrorState title={tc('membersAdmin.errorLoadTitle')} error={membersQuery.error} />;
  }
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined || detailQuery.isLoading) return null;

  const allMembers = membersQuery.data.items;

  // Leave-button gating:
  //   * If the viewer isn't on this circle's roster (parent-network-leader
  //     viewing a child circle via the admin cascade), hide the button
  //     entirely — they can't leave a circle they were never on.
  //   * If they're the sole leader, the button shows disabled with a
  //     tooltip prompting them to promote someone first.
  //   * Otherwise the button is enabled.
  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  // Flat leadership: every `role='leader'` row is equivalent. The pill
  // reads "Leader" / "Member" with no hierarchy. Server-side guards refuse
  // the destructive operations that would leave the circle leaderless;
  // the dropdown surfaces them all and falls back to a banner on error.
  const columns = buildRosterColumns<GroupRosterMember>({
    viewerUserId,
    t,
    fmt,
    groupKind: 'circle',
    renderRole: (m) => {
      const isLeader = m.role === 'leader';
      return (
        <Pill tone={isLeader ? 'burgundy' : 'neutral'}>
          {isLeader ? tc('membersAdmin.roleLeader') : tc('membersAdmin.roleMember')}
        </Pill>
      );
    },
    // Archived Circles are read-only — drop the per-row management menu.
    renderActions: isArchived
      ? null
      : (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={tc('membersAdmin.manageMemberAria', { name })}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                {tc('membersAdmin.promote')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                {tc('membersAdmin.demote')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              {tc('membersAdmin.removeFromCircle')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={tc('membersAdmin.heading')}
        subtitle={tc('membersAdmin.subtitle')}
        actions={
          <Row gap="sm" align="center" wrap>
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content={tc('membersAdmin.onlyLeaderTooltip')}>
                {/* Span so the Tooltip's `asChild` trigger can hang focus +
                 *  hover handlers on a wrapper — Radix discards the events
                 *  on a disabled <button> child. */}
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    {tc('membersAdmin.leaveCircle')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                {tc('membersAdmin.leaveCircle')}
              </Button>
            )}
            {isArchived ? null : (
              <>
                <CopyJoinLinkButton groupId={circleId} />
                <Button
                  variant="primary"
                  size="md"
                  icon={I.plus}
                  onClick={() => setInviteOpen(true)}
                >
                  {tc('membersAdmin.addMember')}
                </Button>
              </>
            )}
          </Row>
        }
      />

      {/* Every viewer who reaches this admin section can restore: a direct
          circle leader, a parent-network leader (the admin cascade —
          `getViewerGroupRole` resolves them to 'leader', and
          `groupRules.restorable` grants them via `parentNetworkLeaderOnArchived`),
          or a platform admin. So the Restore CTA is unconditional here — do
          NOT gate it on `viewerMembership?.role`, which is false for the
          cascade viewer who is not on the child roster and would wrongly hide
          a Restore they are authorized to perform. */}
      {isArchived ? (
        <GroupArchivedCallout groupId={circleId} kind="circle" viewerIsLeader />
      ) : null}

      {membersQuery.data.truncated ? (
        <Body size="sm" color="destructive">
          {t('members.truncated', { maxRows: membersQuery.data.maxRows })}
        </Body>
      ) : null}

      <MembersTablePanel<GroupRosterMember>
        members={allMembers}
        columns={columns}
        t={t}
        searchPlaceholder={tc('membersAdmin.searchPlaceholder')}
        emptyMessage={tc('membersAdmin.emptyTable')}
        totalCount={detailQuery.data?.circle.memberCount ?? allMembers.length}
        countSummary={(shown, total) => (
          <Row justify="space-between" align="center">
            <Body size="sm" color="mute">
              {tc('membersAdmin.countSummary', { count: shown, total })}
            </Body>
            {/* The promote hint points at the per-row menu, which is gone while
                archived — suppress it so the copy never references an action
                that isn't there. */}
            {isArchived ? null : (
              <Body size="sm" color="mute">
                {tc('membersAdmin.promoteHint')}
              </Body>
            )}
          </Row>
        )}
        onExport={exportRoster}
        exportLabel={t('members.export')}
        groupKind="circle"
      />

      {visibleError !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{visibleError}</Body>
        </span>
      ) : null}

      {isArchived ? null : (
        <GroupPendingInvitesCard kind="circle" groupId={circleId} />
      )}

      {isArchived ? null : (
        <InviteMemberModal
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          kind="circle"
          groupId={circleId}
          groupName={circleName}
        />
      )}

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? tc('membersAdmin.removeDialogTitle', { name: memberName(remove.target) })
            : tc('membersAdmin.removeDialogTitleFallback')
        }
        description={
          remove.target !== null
            ? tc('membersAdmin.removeDialogBody', { name: memberName(remove.target) })
            : ''
        }
        actionLabel={tc('membersAdmin.removeDialogConfirm')}
        cancelLabel={tc('membersAdmin.removeDialogCancel')}
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? tc('membersAdmin.demoteDialogTitle', { name: memberName(demote.target) })
            : tc('membersAdmin.demoteDialogTitleFallback')
        }
        description={tc('membersAdmin.demoteDialogBody')}
        actionLabel={tc('membersAdmin.demoteDialogConfirm')}
        cancelLabel={tc('membersAdmin.demoteDialogCancel')}
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title={tc('membersAdmin.leaveDialogTitle')}
        description={tc('membersAdmin.leaveDialogBody')}
        actionLabel={tc('membersAdmin.leaveCircle')}
        cancelLabel={tc('membersAdmin.leaveDialogCancel')}
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}

    </Stack>
  );
}
